// Generated by DemoPack. Do not edit.
import { createDemo, type DemoPack } from '@demopack/demopack';
import type { SupabaseClient } from '@supabase/supabase-js';

const schemaSql = "CREATE TABLE issues (\n  id SERIAL PRIMARY KEY,\n  title TEXT NOT NULL,\n  status TEXT NOT NULL CHECK (status IN ('open', 'in-progress', 'done')),\n  description TEXT,\n  priority INTEGER NOT NULL DEFAULT 0\n);\n\nCREATE TABLE labels (\n  id SERIAL PRIMARY KEY,\n  name TEXT NOT NULL UNIQUE,\n  issue_id INTEGER REFERENCES issues(id)\n);\n";
const seedSql = "-- DEMO_SEED_SENTINEL_issue-board-v1\nINSERT INTO issues (id, title, status, description, priority) VALUES\n  (1, 'Review onboarding flow', 'open', NULL, 1),\n  (2, 'Confirm messaging copy', 'in-progress', 'Needs legal review', 2),\n  (3, 'Publish release checklist', 'done', NULL, 3),\n  (4, 'Triage weekend support queue', 'open', 'Only fictional tickets stay in this demo.', 2),\n  (5, 'Rewrite empty-state illustrations', 'open', 'Keep the art simple and readable.', 1),\n  (6, 'Document reset behavior for visitors', 'in-progress', 'Each tab owns its own in-memory board.', 3),\n  (7, 'Add keyboard hints to the board', 'done', 'Focus rings must stay visible.', 1),\n  (8, 'Classify stale demo comments', 'open', NULL, 0),\n  (9, 'Check pagination with mixed priorities', 'in-progress', 'Twelve seed rows exist so paging is real.', 4),\n  (10, 'Verify search against long titles', 'open', 'Search uses the same ilike filter the SDK emits.', 2),\n  (11, 'Archive completed launch tasks', 'done', 'Done items remain visible until reset.', 0),\n  (12, 'Prepare visitor walkthrough copy', 'open', 'Tell people to create, switch tabs, and reset.', 3);\nSELECT setval('issues_id_seq', (SELECT COALESCE(MAX(id), 0) FROM issues), true);\n\nINSERT INTO labels (id, name, issue_id) VALUES\n  (1, 'docs', 1),\n  (2, 'release', 3),\n  (3, 'a11y', 7),\n  (4, 'demo', 12);\nSELECT setval('labels_id_seq', (SELECT COALESCE(MAX(id), 0) FROM labels), true);\n";
const tables: Parameters<typeof createDemo>[0]['tables'] = {
  "issues": {
    "name": "issues",
    "columns": [
      {
        "name": "id",
        "type": "integer",
        "nullable": false,
        "hasDefault": true
      },
      {
        "name": "title",
        "type": "text",
        "nullable": false,
        "hasDefault": false
      },
      {
        "name": "status",
        "type": "text",
        "nullable": false,
        "hasDefault": false
      },
      {
        "name": "description",
        "type": "text",
        "nullable": true,
        "hasDefault": false
      },
      {
        "name": "priority",
        "type": "integer",
        "nullable": false,
        "hasDefault": true
      }
    ]
  },
  "labels": {
    "name": "labels",
    "columns": [
      {
        "name": "id",
        "type": "integer",
        "nullable": false,
        "hasDefault": true
      },
      {
        "name": "name",
        "type": "text",
        "nullable": false,
        "hasDefault": false
      },
      {
        "name": "issue_id",
        "type": "integer",
        "nullable": true,
        "hasDefault": false
      }
    ]
  }
};
const identity = {
  "id": "demo-visitor",
  "email": "visitor@demo.local",
  "displayName": "Demo Visitor"
} as const;

let instance: DemoPack | null = null;
let initError: Error | null = null;
let initPromise: Promise<DemoPack> | null = null;

export let supabase: SupabaseClient;
export let demo: DemoPack | null = null;

function wantsInvalidSeed(): boolean {
  return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('seed') === 'invalid';
}

async function initialize(): Promise<DemoPack> {
  if (instance) {
    return instance;
  }
  if (initError) {
    throw initError;
  }
  if (!initPromise) {
    initPromise = createDemo({
      schemaSql,
      tables,
      user: identity,
      seed: async (db) => {
        if (wantsInvalidSeed()) {
          throw new Error('Invalid demo seed: fixture is incomplete and cannot start the demo.');
        }
        await db.exec(seedSql);
      },
    }).then((created) => {
      instance = created;
      demo = created;
      supabase = created.supabase;
      return created;
    }).catch((error: unknown) => {
      initError = error instanceof Error ? error : new Error('DemoPack failed to initialize.');
      throw initError;
    });
  }
  return initPromise;
}

export async function getBackend(): Promise<{
  supabase: SupabaseClient;
  demo: DemoPack;
}> {
  const created = await initialize();
  return { supabase: created.supabase, demo: created };
}

export const demoContext = {
  isDemo: true as const,
  user: identity,
  reset: async () => {
    const created = await initialize();
    await created.reset();
  },
  subscribe: (listener: Parameters<DemoPack['subscribe']>[0]) => {
    if (!instance) {
      return () => undefined;
    }
    return instance.subscribe(listener);
  },
  getState: () => instance?.getState() ?? 'initializing',
  getError: () => instance?.getError() ?? initError,
  diagnostics: {
    get observedUnsupported() {
      return instance?.diagnostics.observedUnsupported ?? [];
    },
  },
};
